import React, { useState, useEffect } from "react";
import { X, Loader2 } from "lucide-react";
import ImageUpload from "../common/ImageUpload";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import MultiSelect from "../common/MultiSelect";
import TagInput from "./TagInput";
import useMaster from "../../hooks/useMaster";

const PortfolioForm = ({ initialData, onSubmit, onCancel, isSubmitting }) => {
  const [formData, setFormData] = useState({
    name: "",
    link: "",
    industry_id: [],
    revenue_goal_id: [],
    capability_id: [],
    business_problem_id: [],
    ...initialData,
  });

  const [tags, setTags] = useState(initialData?.tags || []);

  const industries = useMaster("industry");
  const revenueGoals = useMaster("revenue-goal");
  const capabilities = useMaster("capability");
  const businessProblems = useMaster("business-problem");

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        industry_id: Array.isArray(initialData.industry_id) ? initialData.industry_id : [],
        revenue_goal_id: Array.isArray(initialData.revenue_goal_id) ? initialData.revenue_goal_id : [],
        capability_id: Array.isArray(initialData.capability_id) ? initialData.capability_id : [],
        business_problem_id: Array.isArray(initialData.business_problem_id) ? initialData.business_problem_id : []
      }));
      if (initialData.tags) setTags(initialData.tags);
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      tags: tags,
    };

    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 px-1">
      {/* Name Input */}
      <div className="space-y-2">
        <Label htmlFor="name">
          Project Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="name"
          required
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          placeholder="Enter project name"
        />
      </div>

      <ImageUpload
        label={<span>Project Image <span className="font-normal text-xs text-gray-400">(2400 × 4000 px)</span></span>}
        required={!initialData}
        value={formData.image_url}
        onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
      />

      {/* Tags Input */}
      <div className="space-y-2">
        <Label>Tags</Label>
        <TagInput tags={tags} setTags={setTags} />
      </div>

      {/* Link Input */}
      <div className="space-y-2">
        <Label htmlFor="link">Project Link</Label>
        <Input
          id="link"
          type="url"
          value={formData.link}
          onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
          placeholder="https://example.com"
        />
      </div>

      {/* Master Dropdowns (Multi-select) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Industry</Label>
          <MultiSelect
            options={industries.data}
            value={formData.industry_id}
            onChange={(val) => setFormData(prev => ({ ...prev, industry_id: val }))}
            placeholder="Select Industries"
          />
        </div>

        <div className="space-y-2">
          <Label>Revenue Goal</Label>
          <MultiSelect
            options={revenueGoals.data}
            value={formData.revenue_goal_id}
            onChange={(val) => setFormData(prev => ({ ...prev, revenue_goal_id: val }))}
            placeholder="Select Goals"
          />
        </div>

        <div className="space-y-2">
          <Label>Capability</Label>
          <MultiSelect
            options={capabilities.data}
            value={formData.capability_id}
            onChange={(val) => setFormData(prev => ({ ...prev, capability_id: val }))}
            placeholder="Select Capabilities"
          />
        </div>

        <div className="space-y-2">
          <Label>Business Problem</Label>
          <MultiSelect
            options={businessProblems.data}
            value={formData.business_problem_id}
            onChange={(val) => setFormData(prev => ({ ...prev, business_problem_id: val }))}
            placeholder="Select Problems"
          />
        </div>
      </div>


      <div className="flex justify-end gap-3 pt-4 sticky bottom-0 bg-white pb-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting} className="min-w-[100px]">
          {isSubmitting ? (
            <Loader2 className="animate-spin" size={18} />
          ) : initialData ? (
            "Update"
          ) : (
            "Create"
          )}
        </Button>
      </div>
    </form>
  );
};

export default PortfolioForm;
